<script lang="ts">
  import { onMount } from 'svelte';
  import jsQR from 'jsqr';
  import Icon from './Icon.svelte';
  import { parsePartCode } from '../lib/scan';
  import { t } from '../i18n/index.svelte';

  let {
    onResult,
    onClose,
  }: {
    /** selectUnique=true khi QR mã hoá URL deep-link (?partID) → viewer chọn luôn chi tiết nếu khớp duy nhất. */
    onResult: (query: string, exact: boolean, selectUnique?: boolean) => void;
    onClose: () => void;
  } = $props();

  let videoEl: HTMLVideoElement;
  let status = $state(t('scan.opening'));
  let stream: MediaStream | undefined;
  let raf = 0;
  let stopped = false;
  const cnv = document.createElement('canvas');
  const ctx = cnv.getContext('2d', { willReadFrequently: true });

  // ── Zoom vào tem (tem QR quá nhỏ với một số camera) ──
  // Hybrid 2 tầng: (1) zoom PHẦN CỨNG qua applyConstraints({zoom}) — thật, nhiều chi tiết hơn
  // (Chrome/Android); (2) fallback zoom SỐ — phóng hiển thị + crop vùng giữa khung khi quét
  // (iOS Safari thường không có zoom cứng). Xem [[deeplink-partid-url]] cho luồng quét chung.
  const ZOOM_LEVELS = [1, 2, 4];
  let zoom = $state(1); // mức đang chọn (1/2/4)
  let digitalScale = $state(1); // CSS scale trên <video> khi dùng zoom số (1 khi zoom cứng)
  let track: MediaStreamTrack | undefined;
  let hwZoom: { min: number; max: number } | null = null; // capabilities.zoom nếu có
  let cropFactor = 1; // crop vùng giữa 1/cropFactor trước khi jsQR (chỉ khi zoom số)

  async function applyZoom(level: number) {
    zoom = level;
    // Tầng 1: zoom phần cứng (ống kính kéo lại gần thật)
    if (track && hwZoom) {
      const target = Math.min(Math.max(level, hwZoom.min), hwZoom.max);
      try {
        await track.applyConstraints({
          advanced: [{ zoom: target }],
        } as unknown as MediaTrackConstraints);
        digitalScale = 1;
        cropFactor = 1;
        return;
      } catch {
        // một số máy có 'zoom' trong capabilities nhưng applyConstraints vẫn ném → lùi zoom số
      }
    }
    // Tầng 2: zoom số (phóng hiển thị để ngắm + thu hẹp vùng quét)
    digitalScale = level;
    cropFactor = level;
  }

  function stop() {
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
  }

  function handle(text: string) {
    // QR mã hoá URL deep-link (chứa ?partID=) → chọn luôn chi tiết khi khớp duy nhất; tem trần → không.
    const fromUrl = /[?&]partid=/i.test(text);
    const idx = parsePartCode(text);
    if (idx != null) {
      stop();
      onResult(idx, true, fromUrl); // đúng định dạng → tìm ĐÚNG theo mã (STT cắt)
      return;
    }
    // Sai định dạng: có nội dung → tìm theo TOÀN BỘ chuỗi mã; QR rỗng → tiếp tục quét.
    const full = text.trim();
    if (full) {
      stop();
      onResult(full, false, fromUrl);
    } else {
      raf = requestAnimationFrame(scanLoop);
    }
  }

  function scanLoop() {
    if (stopped) return;
    const v = videoEl;
    if (v && v.readyState >= v.HAVE_ENOUGH_DATA && ctx) {
      const w = v.videoWidth;
      const h = v.videoHeight;
      if (w && h) {
        // Zoom số: chỉ quét vùng giữa 1/cropFactor (QR chiếm tỉ lệ lớn hơn, ít nhiễu nền).
        // cropFactor=1 (mặc định / zoom cứng) ⇒ quét nguyên khung.
        const cw = Math.max(1, Math.round(w / cropFactor));
        const ch = Math.max(1, Math.round(h / cropFactor));
        const sx = Math.round((w - cw) / 2);
        const sy = Math.round((h - ch) / 2);
        cnv.width = cw;
        cnv.height = ch;
        ctx.drawImage(v, sx, sy, cw, ch, 0, 0, cw, ch);
        const img = ctx.getImageData(0, 0, cw, ch);
        const code = jsQR(img.data, cw, ch, { inversionAttempts: 'dontInvert' });
        if (code && code.data) {
          handle(code.data);
          return;
        }
      }
    }
    raf = requestAnimationFrame(scanLoop);
  }

  async function start() {
    if (!navigator.mediaDevices?.getUserMedia) {
      status = t('scan.noSupport');
      return;
    }
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // Yêu cầu độ phân giải cao → nhiều điểm ảnh trên QR, giúp cả máy không có zoom cứng.
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      videoEl.srcObject = stream;
      await videoEl.play();
      // Dò khả năng zoom phần cứng của camera (nếu có).
      track = stream.getVideoTracks()[0];
      const caps = track?.getCapabilities?.() as
        | (MediaTrackCapabilities & { zoom?: { min?: number; max?: number } })
        | undefined;
      if (caps?.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > (caps.zoom.min ?? 1)) {
        hwZoom = { min: caps.zoom.min ?? 1, max: caps.zoom.max };
      }
      status = t('scan.aim');
      raf = requestAnimationFrame(scanLoop);
    } catch {
      status = t('scan.noAccess');
    }
  }

  onMount(() => {
    start();
    return () => stop();
  });
</script>

<div
  class="scrim"
  role="button"
  tabindex="-1"
  aria-label={t('common.close')}
  onclick={onClose}
  onkeydown={(e) => e.key === 'Escape' && onClose()}
></div>
<div class="scanner" role="dialog" aria-modal="true" aria-label={t('scan.title')}>
  <div class="head">
    <h2><Icon name="scan" size={18} /> {t('scan.title')}</h2>
    <button class="x" title={t('common.close')} aria-label={t('common.close')} onclick={onClose}><Icon name="close" /></button>
  </div>
  <div class="frame">
    <!-- svelte-ignore a11y_media_has_caption -->
    <video
      bind:this={videoEl}
      playsinline
      muted
      style="transform: scale({digitalScale});"
    ></video>
    <div class="reticle"></div>
    <div class="zoom" role="group" aria-label={t('scan.zoom')}>
      {#each ZOOM_LEVELS as lvl (lvl)}
        <button
          class="zoom-btn"
          class:on={zoom === lvl}
          aria-pressed={zoom === lvl}
          onclick={() => applyZoom(lvl)}
        >{lvl}×</button>
      {/each}
    </div>
  </div>
  <p class="status">{status}</p>
</div>

<style>
  .scrim {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    background: var(--color-overlay-scrim);
    border: none;
  }
  .scanner {
    position: fixed;
    z-index: var(--z-modal);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(380px, calc(100vw - 2 * var(--space-md)));
    padding: var(--space-md);
    background: var(--color-surface);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-sm);
  }
  h2 {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: var(--font-size-title);
    color: var(--color-navy);
  }
  .x {
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 4px;
  }
  .x:hover {
    color: var(--color-error);
  }
  .frame {
    position: relative;
    width: 100%;
    aspect-ratio: 1;
    background: #000;
    border-radius: var(--radius-control);
    overflow: hidden;
  }
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transform-origin: center;
    transition: transform var(--motion-base, 0.15s) ease;
  }
  .zoom {
    position: absolute;
    left: 50%;
    bottom: var(--space-sm);
    transform: translateX(-50%);
    display: flex;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }
  .zoom-btn {
    min-width: 38px;
    padding: 5px 10px;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: #fff;
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 700;
    cursor: pointer;
    line-height: 1;
  }
  .zoom-btn.on {
    background: var(--color-primary-accent);
    color: var(--color-navy);
  }
  .reticle {
    position: absolute;
    inset: 18%;
    border: 3px solid var(--color-primary-accent);
    border-radius: var(--radius-control);
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.25);
  }
  .status {
    margin: var(--space-sm) 0 0;
    text-align: center;
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
    min-height: 2.4em;
  }
</style>
